// Currency utility for live exchange rates
class CurrencyExchange {
  constructor() {
    this.cache = {};
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    this.fallbackRate = 1.10; // Fallback rate if API fails
  }

  // Get live exchange rate from RUB to INR
  async getRubToInrRate() {
    const cacheKey = 'RUB_INR';
    const now = Date.now();
    
    // Check cache first
    if (this.cache[cacheKey] && (now - this.cache[cacheKey].timestamp) < this.cacheExpiry) {
      return this.cache[cacheKey].rate;
    }

    try {
      // Using ExchangeRate-API (free tier: 1500 requests/month)
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/RUB');
      const data = await response.json();
      
      if (data && data.rates && data.rates.INR) {
        const rate = data.rates.INR;
        
        // Cache the result
        this.cache[cacheKey] = {
          rate: rate,
          timestamp: now
        };
        
        return rate;
      }
      
      throw new Error('Invalid response from exchange rate API');
    } catch (error) {
      console.warn('Failed to fetch live exchange rate, using fallback:', error);
      
      // Try alternative API as backup
      try {
        const response = await fetch('https://api.fxratesapi.com/latest?base=RUB&symbols=INR');
        const data = await response.json();
        
        if (data && data.rates && data.rates.INR) {
          const rate = data.rates.INR;
          
          // Cache the result
          this.cache[cacheKey] = {
            rate: rate,
            timestamp: now
          };
          
          return rate;
        }
      } catch (backupError) {
        console.warn('Backup API also failed:', backupError);
      }
      
      // Return fallback rate
      return this.fallbackRate;
    }
  }

  // Get live exchange rate from USD to INR
  async getUsdToInrRate() {
    const cacheKey = 'USD_INR';
    const now = Date.now();
    
    // Check cache first
    if (this.cache[cacheKey] && (now - this.cache[cacheKey].timestamp) < this.cacheExpiry) {
      return this.cache[cacheKey].rate;
    }

    try {
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
      const data = await response.json();
      
      if (data && data.rates && data.rates.INR) {
        const rate = data.rates.INR;
        
        // Cache the result
        this.cache[cacheKey] = {
          rate: rate,
          timestamp: now
        };
        
        return rate;
      }
      
      throw new Error('Invalid response from exchange rate API');
    } catch (error) {
      console.warn('Failed to fetch USD to INR rate, using fallback:', error);
      return 83.50; // Fallback USD to INR rate
    }
  }

  // Convert RUB to INR with live rate
  async convertRubToInr(rubAmount) {
    const rate = await this.getRubToInrRate();
    return Math.round(rubAmount * rate);
  }

  // Convert USD to INR with live rate
  async convertUsdToInr(usdAmount) {
    const rate = await this.getUsdToInrRate();
    return Math.round(usdAmount * rate);
  }

  // Format currency with live conversion
  async formatRubWithLiveInr(rubAmount) {
    const inrAmount = await this.convertRubToInr(rubAmount);
    return {
      rub: `${rubAmount.toLocaleString('en-IN')} RUB`,
      inr: `₹${inrAmount.toLocaleString('en-IN')}`,
      combined: `${rubAmount.toLocaleString('en-IN')} RUB (₹${inrAmount.toLocaleString('en-IN')})`
    };
  }

  // Format USD with live INR conversion
  async formatUsdWithLiveInr(usdAmount) {
    const inrAmount = await this.convertUsdToInr(usdAmount);
    return {
      usd: `$${usdAmount.toLocaleString('en-US')}`,
      inr: `₹${inrAmount.toLocaleString('en-IN')}`,
      combined: `$${usdAmount.toLocaleString('en-US')} (₹${inrAmount.toLocaleString('en-IN')})`
    };
  }

  // Get current exchange rate info for display
  async getExchangeRateInfo() {
    const rubToInr = await this.getRubToInrRate();
    const usdToInr = await this.getUsdToInrRate();
    const lastUpdated = new Date().toLocaleTimeString();
    
    return {
      rubToInr: rubToInr.toFixed(2),
      usdToInr: usdToInr.toFixed(2),
      lastUpdated: lastUpdated,
      isLive: true
    };
  }
}

// Create singleton instance
const currencyExchange = new CurrencyExchange();

// Export utility functions
export const convertRubToInr = (rubAmount) => currencyExchange.convertRubToInr(rubAmount);
export const convertUsdToInr = (usdAmount) => currencyExchange.convertUsdToInr(usdAmount);
export const formatRubWithLiveInr = (rubAmount) => currencyExchange.formatRubWithLiveInr(rubAmount);
export const formatUsdWithLiveInr = (usdAmount) => currencyExchange.formatUsdWithLiveInr(usdAmount);
export const getExchangeRateInfo = () => currencyExchange.getExchangeRateInfo();
export const getRubToInrRate = () => currencyExchange.getRubToInrRate();
export const getUsdToInrRate = () => currencyExchange.getUsdToInrRate();

export default currencyExchange;
